import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, ArrowRight, Target, Calendar, TrendingUp, Users, MessageSquare, Clock, Shield, Zap } from "lucide-react";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";

const BusinessAccelerator = () => {
  const serviceBreakdown = [
    {
      category: "2 Optimized Landing Pages",
      icon: Target,
      items: [
        "Custom-designed high-converting landing pages",
        "Mobile-responsive design optimization",
        "A/B testing for maximum conversion rates",
        "Lead capture form integration",
        "Fast-loading performance optimization"
      ]
    },
    {
      category: "CRM Integration & Optimization",
      icon: Calendar,
      items: [
        "Complete CRM system setup and configuration",
        "Automated lead nurturing sequences",
        "Sales pipeline optimization",
        "Contact management and segmentation",
        "Workflow automation for efficiency"
      ]
    },
    {
      category: "Advanced Lead Generation",
      icon: MessageSquare,
      items: [
        "Multi-platform advertising (Google & Facebook)",
        "Advanced conversion tracking setup",
        "Lead scoring and qualification system",
        "Retargeting campaigns for warm prospects",
        "Custom reporting dashboard"
      ]
    },
    {
      category: "Dedicated Account Management",
      icon: TrendingUp,
      items: [
        "Dedicated account manager assigned",
        "Bi-weekly strategy calls",
        "Priority customer support",
        "Performance optimization reviews",
        "Monthly growth strategy sessions"
      ]
    }
  ];

  const guarantees = [
    {
      icon: Shield,
      title: "90-Day Money-Back Guarantee",
      description: "If you don't see qualified leads within 90 days, we'll refund your investment completely."
    },
    {
      icon: Clock,
      title: "Setup Within 14 Days",
      description: "Your complete accelerated system will be live and generating leads within two weeks."
    },
    {
      icon: Zap,
      title: "50% Lead Increase Within 60 Days",
      description: "Most clients see a 50% increase in qualified leads within the first 60 days."
    }
  ];

  return (
    <>
      <EnhancedSEO 
        title="Business Accelerator Package - Complete Growth Solution | VGP"
        description="Accelerate your business growth with our comprehensive Business Accelerator program. Advanced lead generation, sales funnel optimization, and dedicated account management."
        keywords="business accelerator program, advanced lead generation, sales funnel optimization, dedicated account manager, service business growth"
        url="/business-accelerator"
      />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-background via-secondary/10 to-accent/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="secondary" className="mb-6 text-lg px-6 py-2 bg-gradient-to-r from-primary/20 to-secondary/20 text-primary border-primary/30">
                Most Popular - Business Accelerator Package
              </Badge>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                Complete Business Growth
                <span className="bg-gradient-to-r from-secondary to-accent bg-clip-text text-transparent"> Accelerator System</span>
              </h1>
              
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8 leading-relaxed">
                Scale rapidly with our comprehensive Business Accelerator program - advanced lead generation, sales automation, and dedicated account management to dominate your market.
              </p>

              <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-accent" />
                  <span>14-Day Setup</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-accent" />
                  <span>90-Day Guarantee</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-accent" />
                  <span>Dedicated Account Manager</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Service Breakdown */}
        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                  Everything You Get in Your Business Accelerator System
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  A complete breakdown of every component that makes up your advanced business acceleration machine.
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-8">
                {serviceBreakdown.map((service, index) => {
                  const IconComponent = service.icon;
                  return (
                    <Card key={index} className="border-border/50 hover:border-border transition-colors">
                      <CardHeader>
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-secondary to-accent rounded-lg flex items-center justify-center">
                            <IconComponent className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-xl text-foreground">{service.category}</CardTitle>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-3">
                          {service.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                              <span className="text-muted-foreground">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Investment & Guarantees */}
        <section className="py-16 bg-secondary/5">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                  Your Investment & Guarantees
                </h2>
                <p className="text-lg text-muted-foreground">
                  We're so confident in our accelerated system, we back it with iron-clad guarantees.
                </p>
              </div>

              {/* Investment */}
              <Card className="mb-12 border-2 border-accent/20">
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl text-foreground mb-2">Business Accelerator Investment</CardTitle>
                  <div className="text-4xl md:text-5xl font-bold text-accent mb-2">£1,499</div>
                  <CardDescription className="text-lg">
                    Per month with bi-weekly strategy calls and dedicated account management
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-accent/10 rounded-lg p-6 text-center">
                    <p className="text-muted-foreground mb-4">
                      <strong>Most Popular Choice:</strong> Comprehensive growth solution for ambitious businesses
                    </p>
                    <div className="text-2xl font-bold text-foreground">Everything in Growth Starter +</div>
                    <div className="text-lg text-accent mt-2">Advanced automation & dedicated support</div>
                  </div>
                </CardContent>
              </Card>

              {/* Guarantees */}
              <div className="grid md:grid-cols-3 gap-6">
                {guarantees.map((guarantee, index) => {
                  const IconComponent = guarantee.icon;
                  return (
                    <Card key={index} className="text-center border-border/50">
                      <CardHeader>
                        <div className="w-16 h-16 bg-gradient-to-br from-secondary to-accent rounded-full flex items-center justify-center mx-auto mb-4">
                          <IconComponent className="w-8 h-8 text-primary" />
                        </div>
                        <CardTitle className="text-lg text-foreground">{guarantee.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription>{guarantee.description}</CardDescription>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-primary">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-4xl mx-auto text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Accelerate Your Business Growth?
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Book your dedicated Business Accelerator consultation to discuss your specific needs and get your advanced growth system set up within 14 days.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
                <Button 
                  asChild 
                  variant="secondary" 
                  size="xl"
                  className="group transform hover:scale-105 transition-all duration-300"
                >
                  <a href="/do-you-qualify">
                    Schedule Accelerator Consultation
                    <ArrowRight className="w-6 h-6 ml-2 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
                
                <span className="text-white/60 text-sm">or</span>
                
                <Button 
                  asChild 
                  variant="outline" 
                  size="xl"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20 transform hover:scale-105 transition-all duration-300"
                >
                  <a href="tel:+447857450763">
                    Call Direct: 07857 450763
                  </a>
                </Button>
              </div>
              
              <p className="text-white/80 text-sm mt-4">
                Dedicated Business Accelerator consultation • Advanced strategy • 14-day setup guarantee • Call us directly at 07857 450763
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BusinessAccelerator;